'use client';

import { Checkbox as BaseCheckbox } from '@base-ui-components/react/checkbox';
import { cva, type VariantProps } from 'class-variance-authority';
import { Check, Minus } from 'lucide-react';

import { cn } from '../../utils/cn';
import './checkbox.css';

const checkboxVariants = cva(
  'checkbox-base inline-flex items-center justify-center peer shrink-0 rounded-sm border border-primary shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 data-[checked]:bg-primary data-[checked]:text-primary-foreground',
  {
    variants: {
      size: {
        default: 'h-4 w-4',
        lg: 'h-5 w-5',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  }
);

interface CheckboxProps extends VariantProps<typeof checkboxVariants> {
  checked?: boolean;
  defaultChecked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  indeterminate?: boolean;
  disabled?: boolean;
  required?: boolean;
  name?: string;
  value?: string;
  className?: string;
  id?: string;
}

function Checkbox({
  className,
  size,
  checked,
  defaultChecked,
  onCheckedChange,
  indeterminate,
  ...props
}: CheckboxProps & { ref?: React.Ref<HTMLElement> }) {
  const rootProps = {
    checked,
    defaultChecked,
    onCheckedChange,
    indeterminate,
    ...props,
  } as React.ComponentProps<typeof BaseCheckbox.Root>;

  return (
    <BaseCheckbox.Root
      className={cn(checkboxVariants({ size }), className)}
      {...rootProps}
    >
      <BaseCheckbox.Indicator className="checkbox-indicator flex items-center justify-center text-current">
        {indeterminate ? (
          <Minus className={size === 'lg' ? 'h-4 w-4' : 'h-3 w-3'} />
        ) : (
          <Check className={size === 'lg' ? 'h-4 w-4' : 'h-3 w-3'} />
        )}
      </BaseCheckbox.Indicator>
    </BaseCheckbox.Root>
  );
}

export { Checkbox, checkboxVariants };
export type { CheckboxProps };
